import { Outlet, NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { bg, line, tx, font, accent, space, radius } from '../theme';
import { LogoLockup } from '../components/Logo';
import Button from '../components/Button';
import { ClubProvider, useClub } from './ClubContext';
import ClubCrest from './ClubCrest';

const TIER_NAMES = { 1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two' };
const SOON = ['Tactics', 'Scouting', 'Finances', 'Fixtures'];

function NavItem({ to, label, end, disabled }) {
  const base = {
    display: 'block', padding: `${space.s2}px ${space.s3}px`, borderRadius: radius.md,
    fontFamily: font.display, fontSize: 13, fontWeight: 600, letterSpacing: '0.04em',
    textDecoration: 'none', marginBottom: 2,
  };
  if (disabled) {
    return (
      <div style={{ ...base, color: tx.faint, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {label}
        <span style={{ fontFamily: font.data, fontSize: 9, letterSpacing: '0.1em', color: tx.faint }}>SOON</span>
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        ...base,
        color: isActive ? bg.base : tx.secondary,
        background: isActive ? accent.lime : 'transparent',
      })}
    >
      {label}
    </NavLink>
  );
}

function Sidebar({ saveId }) {
  const { user, signOut } = useAuth();
  const { club, status } = useClub();
  const navigate = useNavigate();
  const ready = status === 'ready';

  return (
    <aside style={{
      width: 232, flexShrink: 0, background: bg.surface,
      borderRight: `1px solid ${line.DEFAULT}`, display: 'flex', flexDirection: 'column',
      position: 'sticky', top: 0, height: '100vh',
    }}>
      <div style={{ padding: space.s4, borderBottom: `1px solid ${line.DEFAULT}` }}>
        <LogoLockup markHeight={18} />
      </div>

      {ready && club && (
        <div style={{
          padding: space.s4, borderBottom: `1px solid ${line.DEFAULT}`,
          display: 'flex', alignItems: 'center', gap: space.s3,
        }}>
          <ClubCrest club={club} size={38} />
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: font.display, fontWeight: 700, fontSize: 14, color: tx.primary,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{club.name}</div>
            <div style={{ fontFamily: font.data, fontSize: 10.5, color: tx.muted }}>
              {TIER_NAMES[club.tier] || `Tier ${club.tier}`}
            </div>
          </div>
        </div>
      )}

      <nav style={{ padding: space.s3, flex: 1 }}>
        <NavItem to={`/game/${saveId}`} label="Dashboard" end disabled={!ready} />
        <NavItem to={`/game/${saveId}/squad`} label="Squad" disabled={!ready} />
        <div style={{ height: 1, background: line.DEFAULT, margin: `${space.s3}px ${space.s2}px` }} />
        {SOON.map((label) => <NavItem key={label} label={label} disabled />)}
      </nav>

      <div style={{ padding: space.s3, borderTop: `1px solid ${line.DEFAULT}` }}>
        <button
          onClick={() => navigate('/hub')}
          style={{
            width: '100%', background: 'transparent', border: 'none', textAlign: 'left',
            cursor: 'pointer', color: tx.secondary, fontFamily: font.display, fontSize: 12,
            fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: `${space.s2}px ${space.s3}px`, marginBottom: space.s2,
          }}
        >
          &lsaquo; All saves
        </button>
        <div style={{
          fontFamily: font.data, fontSize: 10.5, color: tx.faint,
          padding: `0 ${space.s3}px`, marginBottom: space.s3, wordBreak: 'break-all',
        }}>
          {user?.email}
        </div>
        <Button variant="ghost" small onClick={signOut} style={{ width: '100%', justifyContent: 'center' }}>
          Sign out
        </Button>
      </div>
    </aside>
  );
}

export default function GameShell() {
  const { saveId } = useParams();
  return (
    <ClubProvider saveId={saveId}>
      <div style={{ minHeight: '100vh', display: 'flex', background: bg.base }}>
        <Sidebar saveId={saveId} />
        <main style={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </main>
      </div>
    </ClubProvider>
  );
}
